/**
 * voice-v8 — Werkzeugkarten: Unterwegs: Wetter, Standort, ÖBB-Verbindungen, Route.
 *
 * Herausgelöst aus lib/tool-cards.ts (15.10.2026, Plan „Karten zu 100 % einheitlich“, Welle 3) — Verhalten unverändert;
 * der eine Einstieg bleibt `buildToolCard` in lib/tool-cards.ts.
 */
import { type IconName, icon } from "../icons.js";
import { baueKarte, fehlerBlock, hinweisKarte, kartenTitel } from "../karte.js";
import { trimText, unwrapResult } from "./hilfe.js";

/*
 * 14.10.2026: Wurzel, Titel, Wertzeile, Fehlerkasten und Link kommen aus
 * lib/karte.ts — dem einen Grundgerüst aller Karten. Hier stand bis dahin
 * je eine eigene Fassung; die Faltung (schritte.ts) und die Liedkarte
 * bauten dieselben Teile ein drittes Mal.
 */

// --- Specialised cards ---------------------------------------------------

interface WeatherResult {
  location?: { name?: string; country?: string };
  current?: {
    temperature?: number;
    description?: string;
    windKmh?: number;
    time?: string;
  };
  forecast?: Array<{
    date: string;
    description: string;
    tempMax: number;
    tempMin: number;
    precipitationMm: number;
  }>;
  speak?: string;
}

// Wetter-Beschreibung -> feines Linien-Icon (eigene Icon-Sprache).
function weatherIcon(desc: string): IconName {
  const d = (desc || "").toLowerCase();
  if (/gewitter|blitz/.test(d)) return "cloud-lightning";
  if (/schnee|flocke|graupel/.test(d)) return "cloud-snow";
  if (/regen|schauer|niesel|guss/.test(d)) return "cloud-rain";
  if (/bedeckt|stark bew\u00f6lkt|tr\u00fcb|nebel|neblig/.test(d)) return "cloud";
  if (/sonnig|klar|heiter|wolkenlos/.test(d)) return "sun";
  return "cloud-sun"; // teils bew\u00f6lkt / wechselnd / Default
}

export function buildWeatherCard(result: unknown): HTMLElement {
  const r = (result ?? {}) as WeatherResult & { error?: string };
  // 2026-07-25 (Martin: "was bedeutet dieser Strich?"): Ohne verwertbare Daten
  // KEINE Karte. Frueher entstand daraus eine leere Karte mit dem Platzhalter
  // "Dein Standort" und einem Strich statt der Temperatur. Eine dezente Zeile
  // sagt mehr als eine Karte, die so tut als haette sie etwas zu zeigen.
  if (typeof r.current?.temperature !== "number") {
    const grund = typeof r.error === "string" && r.error ? r.error : "";
    return hinweisKarte(
      grund ? `Wetter: ${trimText(grund, 80)}` : "Wetter gerade nicht verfügbar.",
    );
  }
  const el = baueKarte("weather");
  const where = r.location?.name ?? "Dein Standort";
  el.appendChild(kartenTitel(where));

  const c = r.current ?? {};
  const hero = document.createElement("div");
  hero.className = "tool-weather-hero";
  const now = document.createElement("div");
  now.className = "tool-weather-now";
  const temp = document.createElement("span");
  temp.className = "tool-weather-temp";
  temp.textContent =
    typeof c.temperature === "number" ? `${Math.round(c.temperature)}°` : "—";
  now.appendChild(temp);
  hero.appendChild(now);
  if (c.description) {
    const desc = document.createElement("div");
    desc.className = "tool-weather-desc";
    desc.textContent = String(c.description);
    hero.appendChild(desc);
  }
  el.appendChild(hero);

  if (r.forecast && r.forecast.length > 0) {
    const list = document.createElement("ul");
    list.className = "tool-weather-forecast";
    for (const day of r.forecast.slice(0, 5)) {
      const li = document.createElement("li");
      const dt = new Date(day.date);
      const dow = isNaN(dt.getTime())
        ? String(day.date)
        : dt.toLocaleDateString("de-DE", { weekday: "short" }).replace(/\.$/, "");
      const dowEl = document.createElement("span");
      dowEl.className = "twf-dow";
      dowEl.textContent = dow;
      const icoEl = document.createElement("span");
      icoEl.className = "twf-ico";
      icoEl.appendChild(
        icon(weatherIcon(String(day.description ?? "")), { size: 23, stroke: 1.75 }),
      );
      const hi = document.createElement("span");
      hi.className = "twf-hi";
      hi.textContent = `${Math.round(day.tempMax)}°`;
      const lo = document.createElement("span");
      lo.className = "twf-lo";
      lo.textContent = `${Math.round(day.tempMin)}°`;
      li.append(dowEl, icoEl, hi, lo);
      list.appendChild(li);
    }
    el.appendChild(list);
  }
  return el;
}

// 2026-07-01 (Martin): Schoene Standort-Card statt Koordinaten-Dump —
// Ortsname + Land + klickbare Mini-Map (oeffnet gross). Wie die Wetter-Card.
interface LocationResult {
  hasLocation?: boolean;
  lat?: number;
  lng?: number;
  city?: string;
  country?: string;
}
export function buildLocationCard(result: unknown): HTMLElement {
  const r = (result ?? {}) as LocationResult;
  const el = baueKarte("location");
  el.appendChild(kartenTitel("Standort"));
  if (
    r.hasLocation === false ||
    typeof r.lat !== "number" ||
    typeof r.lng !== "number"
  ) {
    return hinweisKarte("Standort gerade nicht verfügbar.");
  }
  const lat = r.lat;
  const lng = r.lng;
  const place = (r.city ?? "").trim();
  const country = (r.country ?? "").trim();
  const nameEl = document.createElement("div");
  nameEl.className = "tool-loc-place";
  nameEl.textContent = place || "Unbekannter Ort";
  el.appendChild(nameEl);
  if (country) {
    const ctry = document.createElement("div");
    ctry.className = "tool-loc-country";
    ctry.textContent = country;
    el.appendChild(ctry);
  }
  const mapBtn = document.createElement("a");
  mapBtn.className = "tool-loc-map";
  mapBtn.href = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  mapBtn.target = "_blank";
  mapBtn.rel = "noopener noreferrer";
  const img = document.createElement("img");
  img.className = "tool-loc-map-img";
  img.alt = "Karte";
  img.loading = "lazy";
  // 2026-07-04: eigener keyless Renderer — der externe Dienst ist tot (weisses Feld).
  img.src = `/api/map/static?lat=${lat}&lng=${lng}&zoom=13`;
  img.addEventListener("error", () => mapBtn.classList.add("is-failed"));
  mapBtn.appendChild(img);
  const hint = document.createElement("span");
  hint.className = "tool-loc-map-hint";
  hint.textContent = "Groß anzeigen";
  mapBtn.appendChild(hint);
  el.appendChild(mapBtn);
  return el;
}

// Pre.312: ÖBB-Zugverbindungen
interface OebbJourneyItem {
  departure?: string;
  arrival?: string;
  durationMin?: number;
  transfers?: number;
  lines?: string[];
  fromName?: string;
  toName?: string;
  platforms?: { from?: string | null; to?: string | null };
}
interface OebbResultShape {
  ok?: boolean;
  resolved?: { from?: string; to?: string; when?: string | null };
  journeys?: OebbJourneyItem[];
  error?: string;
}

/** CLI/MCP liefert oft JSON-String. Ohne Unwrap lügt die Karte. */
export function leseOebbErgebnis(result: unknown): OebbResultShape {
  const r = unwrapResult(result) as OebbResultShape & { result?: unknown };
  const inner =
    r &&
    typeof r.result === "object" &&
    r.result !== null &&
    !Array.isArray(r.journeys)
      ? (unwrapResult(r.result) as OebbResultShape)
      : r;
  const journeys = Array.isArray(inner.journeys) ? inner.journeys : [];
  return {
    ok: inner.ok !== false && journeys.length > 0,
    journeys,
    error: typeof inner.error === "string" ? inner.error : undefined,
    resolved: inner.resolved,
  };
}

export function oebbHatZuege(result: unknown): boolean {
  return (leseOebbErgebnis(result).journeys?.length ?? 0) > 0;
}

export function waehleFinaleOebbKarte<T extends { result: unknown }>(
  karten: T[],
): T | null {
  if (!karten.length) return null;
  for (let i = karten.length - 1; i >= 0; i--) {
    if (oebbHatZuege(karten[i].result)) return karten[i];
  }
  return karten[karten.length - 1];
}

export function buildOebbCard(result: unknown): HTMLElement {
  const r = leseOebbErgebnis(result);
  const el = baueKarte("oebb");
  el.appendChild(kartenTitel("ÖBB · Zugverbindung"));

  if (!r.ok || !r.journeys || r.journeys.length === 0) {
    // 14.10.2026: der eine Fehlerkasten des Grundgerüsts — keine eigene ÖBB-Klasse mehr.
    el.appendChild(fehlerBlock(r.error ?? "Keine Verbindung gefunden."));
    return el;
  }

  const route = document.createElement("p");
  route.className = "tool-oebb-route";
  const wann = r.resolved?.when ? fmtOebbWhen(r.resolved.when) : "";
  route.textContent = wann
    ? `${r.resolved?.from ?? "?"} → ${r.resolved?.to ?? "?"} · ${wann}`
    : `${r.resolved?.from ?? "?"} → ${r.resolved?.to ?? "?"}`;
  el.appendChild(route);

  const list = document.createElement("ul");
  list.className = "tool-oebb-list";
  for (const j of r.journeys.slice(0, 3)) {
    const li = document.createElement("li");
    li.className = "tool-oebb-item";
    const dep = j.departure ? fmtTime(j.departure) : "—";
    const arr = j.arrival ? fmtTime(j.arrival) : "—";
    // Pre.790 (#60): geteiltes fmtDuration (killt "0h 45m"-Anzeigen).
    const dur =
      typeof j.durationMin === "number" ? fmtDuration(j.durationMin) : "?";
    const tx =
      (j.transfers ?? 0) === 0 ? "direkt" : `${j.transfers} Umstieg(e)`;
    const lines = j.lines && j.lines.length > 0 ? j.lines.join(" → ") : "—";
    const top = document.createElement("div");
    top.className = "tool-oebb-times";
    const depStrong = document.createElement("strong");
    depStrong.textContent = dep;
    const arrStrong = document.createElement("strong");
    arrStrong.textContent = arr;
    top.appendChild(depStrong);
    top.appendChild(document.createTextNode(" → "));
    top.appendChild(arrStrong);
    top.appendChild(document.createTextNode(` · ${dur}`));
    const sub = document.createElement("div");
    sub.className = "tool-oebb-meta";
    sub.textContent = `${lines} · ${tx}`;
    li.appendChild(top);
    li.appendChild(sub);
    list.appendChild(li);
  }
  el.appendChild(list);
  return el;
}

function fmtOebbWhen(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("de-AT", {
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
    });
  } catch {
    return "";
  }
}

function fmtTime(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("de-DE", {
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

// Pre.313: route_directions — Distanz/Dauer-Card mit Maps-Link
interface RouteResultShape {
  ok?: boolean;
  origin?: string;
  destination?: string;
  mode?: string;
  distanceKm?: number;
  durationMin?: number;
  mapsUrl?: string;
  autoOeffnen?: boolean;
  error?: string;
}

// Pre.790 (#60): EINE Dauer-Formatierung für Route + ÖBB (vorher Inline-
// Duplikat in beiden Buildern; killt auch das hässliche "0h 45m").
function fmtDuration(min: number): string {
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return h === 0 ? `${m} min` : m === 0 ? `${h} h` : `${h} h ${m} min`;
}

// Google Routes TravelMode — normalizeMode mappt serverseitig alles
// andere auf DRIVE, also exakt diese vier.
const ROUTE_MODE_LABELS: Record<string, string> = {
  DRIVE: "Auto",
  WALK: "Zu Fuß",
  BICYCLE: "Rad",
  TRANSIT: "Öffis",
};

/*
 * Die Fehlercodes des Werkzeugs (tools/route_directions.ts) als Satz. Der
 * `hint` daneben spricht das Modell an („nutze web_search“), nicht Martin.
 */
const ROUTE_FEHLER_SAETZE: Record<string, string> = {
  "kein-ort": "Das Ziel ist kein Ort.",
  "kein-standort": "Kein Startort genannt und kein GPS-Standort bekannt.",
  "nichts-in-der-naehe": "In der Nähe wurde nichts gefunden.",
};

/** Was die Karte zu sagen hat — eine Zeile, nie den Rohtext, nie eine Erfindung. */
export function routeKartenText(result: unknown): string | null {
  const r = unwrapResult(result) as RouteResultShape;
  if (r.ok === false || typeof r.error === "string") {
    const grund = typeof r.error === "string" ? r.error.trim() : "";
    const satz = grund ? (ROUTE_FEHLER_SAETZE[grund] ?? grund) : "Die Route konnte nicht berechnet werden.";
    return `Route: ${trimText(satz, 120)}`;
  }
  if (typeof r.distanceKm === "number") return null;
  return "Route: Das Ergebnis konnte nicht angezeigt werden.";
}

export function buildRouteCard(result: unknown): HTMLElement {
  // Pre.790 (#60): aufgewertet — Ziel-Kopf mit Karten-Icon, Distanz groß
  // (Wetter-Muster), Modus-Pill, Button im erprobten tool-action-Muster.
  /*
   * ⚠️ 14.10.2026 (Martin, Bild „Route · 2-mal“ mit zweimal „Keine Route
   *    gefunden.“): Das Werkzeug liefert JSON-TEXT, und über die Brücke
   *    kommt er unverändert an — auch in den Verlauf. Die Karte las den Text
   *    als Objekt: kein `distanceKm`, kein `error`, also der Standardtext —
   *    für fünf von sechs Routen des Tages, die alle berechnet waren.
   *    Darum hier wie bei jeder anderen Karte zuerst auspacken, und ein
   *    Fehlersatz nur, wenn das Werkzeug einen gemeldet hat.
   */
  const r = unwrapResult(result) as RouteResultShape;
  const el = baueKarte("route");
  el.appendChild(kartenTitel("Route"));

  const hinweis = routeKartenText(result);
  if (hinweis !== null || typeof r.distanceKm !== "number") {
    return hinweisKarte(hinweis ?? "Route: Das Ergebnis konnte nicht angezeigt werden.");
  }

  const head = document.createElement("div");
  head.className = "tool-route-head";
  const ico = document.createElement("span");
  ico.className = "tool-route-icon";
  ico.appendChild(icon("map-pin", { size: 18 }));
  head.appendChild(ico);
  const body = document.createElement("div");
  body.className = "tool-route-body";
  const dest = document.createElement("div");
  dest.className = "tool-route-dest";
  dest.textContent = r.destination ?? "?";
  body.appendChild(dest);
  if (r.origin) {
    const from = document.createElement("div");
    from.className = "tool-route-from";
    from.textContent = `von ${r.origin}`;
    body.appendChild(from);
  }
  head.appendChild(body);
  el.appendChild(head);

  const stats = document.createElement("div");
  stats.className = "tool-route-stats";
  const km = document.createElement("span");
  km.className = "tool-route-km";
  km.textContent = `${r.distanceKm.toLocaleString("de-DE", { maximumFractionDigits: 1 })} km`;
  stats.appendChild(km);
  if (typeof r.durationMin === "number") {
    const dur = document.createElement("span");
    dur.className = "tool-route-dur";
    dur.textContent = fmtDuration(r.durationMin);
    stats.appendChild(dur);
  }
  const modeLabel = r.mode ? ROUTE_MODE_LABELS[r.mode] : undefined;
  if (modeLabel) {
    const mode = document.createElement("span");
    mode.className = "tool-route-mode";
    mode.textContent = modeLabel;
    stats.appendChild(mode);
  }
  el.appendChild(stats);

  if (r.mapsUrl) {
    const actions = document.createElement("div");
    actions.className = "tool-actions";
    const link = document.createElement("a");
    link.className = "tool-action-btn primary";
    link.href = r.mapsUrl;
    link.target = "_blank";
    link.rel = "noopener noreferrer";
    // Start-Wunsch: das ist der Rückfall, falls das Selbst-Öffnen scheitert.
    link.textContent = r.autoOeffnen === true ? "Navigation starten" : "In Google Maps öffnen";
    actions.appendChild(link);
    el.appendChild(actions);
  }
  return el;
}
